import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import PeopleTable from "../../People/PeopleTable";
import PeopleTableSkeleton from "../../People/PeopleTableSkeleton";
import TagToolbar from "./TagToolbar";
import TagCreateModal from "./TagCreateModal";
import TagTypeChip from "./TagTypeChip";
import { TAG_OPEN_CREATE_MODAL } from "./tagEvents";
import {
  buildCreateTagPayload,
  formatTagName,
  mapTagFromApi,
} from "./tagUtils";
import { clearTags, createTag, deleteTag, fetchTags } from "../../../../store/features/tagsApi";
import CustomerDeleteModal from "../../People/Customers/CustomerDeleteModal";
import { COLORS, FONT } from "../shared/styles";

function TagManagementPage() {
  const dispatch = useDispatch();
  const businessId = useSelector((state) => state.loginToken?.response?.user?.business_id);
  const { list, loading, saving, deleting } = useSelector((state) => state.tags);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const rowsPerPage = 10;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadTags = useCallback(() => {
    if (!businessId) return;
    dispatch(
      fetchTags({
        businessId,
        type: typeFilter,
        search: debouncedSearch,
      }),
    );
  }, [businessId, debouncedSearch, dispatch, typeFilter]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  useEffect(() => () => dispatch(clearTags()), [dispatch]);

  useEffect(() => {
    const openCreate = () => setCreateModalOpen(true);
    window.addEventListener(TAG_OPEN_CREATE_MODAL, openCreate);
    return () => window.removeEventListener(TAG_OPEN_CREATE_MODAL, openCreate);
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search, typeFilter]);

  const tableRows = useMemo(() => list.map(mapTagFromApi), [list]);

  const handleCreateTag = async ({ name, tagType }) => {
    const trimmedName = name.trim();
    const tagRegex = /^#?[A-Za-z0-9]+(?:[ @#-][A-Za-z0-9]+)*$/;

    if (!tagRegex.test(trimmedName)) {
      toast.error("Tag can only include letters, numbers, spaces, @, #, or hyphens");
      return;
    }

    if (trimmedName.length > 32) {
      toast.error("Tag name cannot exceed 32 characters.");
      return;
    }

    if (!businessId) {
      toast.error("Business id is missing.");
      return;
    }

    try {
      const result = await dispatch(
        createTag(buildCreateTagPayload({ name: trimmedName, tagType, businessId })),
      ).unwrap();

      if (result?.success === false) {
        toast.error(result?.message || "Failed to create tag.");
        return;
      }

      toast.success(result?.message || "Tag created successfully!");
      setCreateModalOpen(false);
      loadTags();
    } catch (error) {
      toast.error(error?.message || "Failed to create tag.");
    }
  };

  const handleDeleteRequest = (row) => {
    setDeleteTarget(row);
  };

  const closeDeleteModal = () => {
    if (!deleting) {
      setDeleteTarget(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.tagId) return;

    try {
      const result = await dispatch(deleteTag({ tagId: deleteTarget.tagId })).unwrap();

      if (result?.success === false) {
        toast.error(result?.message || "Failed to delete tag.");
        return;
      }

      toast.success(result?.message || "Tag deleted successfully!");
      setDeleteTarget(null);
      loadTags();
    } catch (error) {
      toast.error(error?.message || "Failed to delete tag.");
    }
  };

  const actionMenu = [{ key: "delete", label: "Delete Tag", danger: true }];

  const onAction = (type, row) => {
    if (type === "delete") {
      handleDeleteRequest(row);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Tag Name",
      width: "minmax(160px, 1.2fr)",
      render: (row) => (
        <Typography
          sx={{
            color: COLORS.textPrimary,
            fontFamily: FONT.family,
            fontSize: 14,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {formatTagName(row.name)}
        </Typography>
      ),
    },
    {
      key: "type",
      label: "Type",
      width: "minmax(140px, 0.9fr)",
      render: (row) => <TagTypeChip label={row.typeLabel} />,
    },
    {
      key: "usage",
      label: "Usage",
      width: "minmax(80px, 0.5fr)",
    },
    {
      key: "createdBy",
      label: "Created By",
      width: "minmax(160px, 1.1fr)",
    },
    {
      key: "createdDate",
      label: "Created Date",
      width: "minmax(120px, 0.7fr)",
    },
  ];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <TagToolbar
        searchQuery={search}
        onSearchChange={setSearch}
        typeFilter={typeFilter}
        onTypeFilterChange={setTypeFilter}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          width: "100%",
        }}
      >
        {loading ? (
          <PeopleTableSkeleton columns={columns} selectable={false} />
        ) : (
          <PeopleTable
            columns={columns}
            rows={tableRows}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={setPage}
            actionMenu={actionMenu}
            onAction={onAction}
            entityLabel="Tags"
            selectable={false}
          />
        )}
      </Box>

      <TagCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onConfirm={handleCreateTag}
        submitting={saving}
      />

      <CustomerDeleteModal
        open={Boolean(deleteTarget)}
        title="Delete Tag"
        customerName={deleteTarget ? formatTagName(deleteTarget.name) : undefined}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}

export default TagManagementPage;
